import { useEffect, useRef, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Search, X } from 'lucide-react';
import { Input } from '@artstorefronts/ui';

const SEARCH_USERS = gql`
  query BountyAdminUserSearch($search: String) {
    users(search: $search, limit: 8) {
      users {
        asfId
        firstName
        lastName
        email
      }
    }
  }
`;

export type PickedMember = { asfId: string; firstName: string | null; lastName: string | null; email: string };

export function memberLabel(user: PickedMember): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} — ${user.email}` : user.email;
}

/**
 * Find one member by name or email — the quest lookup's search, for the bounty pages'
 * lookup and invite panel. Calls `onPick` with the chosen member, or null when cleared.
 */
export function MemberPicker({
  onPick,
  picked,
  testId,
}: {
  onPick: (member: PickedMember | null) => void;
  picked: PickedMember | null;
  testId: string;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [runSearch, { data, loading }] = useLazyQuery<{ users: { users: PickedMember[] } }>(SEARCH_USERS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const trimmed = term.trim();
    if (picked || trimmed.length < 2) {
      setOpen(false);
      return;
    }
    const handle = window.setTimeout(() => {
      void runSearch({ variables: { search: trimmed } });
      setOpen(true);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [term, picked, runSearch]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const matches = data?.users.users ?? [];

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <Input
        className="pl-8"
        placeholder="Search by name or email…"
        value={picked ? memberLabel(picked) : term}
        readOnly={Boolean(picked)}
        data-testid={testId}
        onChange={(event) => setTerm(event.target.value)}
      />
      {picked ? (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          aria-label="Clear selected member"
          onClick={() => {
            onPick(null);
            setTerm('');
          }}
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
      {open && !picked ? (
        <ul className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? <li className="px-3 py-2 text-sm text-gray-400">Searching…</li> : null}
          {!loading && matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">No members found</li>
          ) : null}
          {matches.map((user) => (
            <li key={user.asfId}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                onClick={() => {
                  onPick(user);
                  setOpen(false);
                }}
              >
                {memberLabel(user)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
